import React, { useEffect, useRef, useState } from 'react';
import { Bell, CheckCircle, ChevronLeft, ChevronRight, ExclamationCircle, Envelope, Upload, ArrowRepeat, X } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
   ImportAttempt, importAttemptsApi, isImportIssue, useGetImportActivityInfiniteQuery,
   useGetImportSummaryQuery, useMarkAllImportsReadMutation, useMarkImportReadMutation,
} from '../../store/import-attempts/import-attempts.api';
import { invoicesApi } from '../../store/invoices/invoices.api';
import type { AppDispatch } from '../../store';
import './notification-bell.scss';

const NotificationBell = () => {
   const { t, i18n } = useTranslation('notifications');
   const dispatch = useDispatch<AppDispatch>();
   const [open, setOpen] = useState(false);
   const [unreadOnly, setUnreadOnly] = useState(false);
   const [selected, setSelected] = useState<ImportAttempt | null>(null);
   const [pollInterval, setPollInterval] = useState(60_000);
   const [pageVisible, setPageVisible] = useState(() => document.visibilityState !== 'hidden');
   const [actionError, setActionError] = useState(false);
   const container = useRef<HTMLDivElement>(null);
   const bell = useRef<HTMLButtonElement>(null);
   const closeButton = useRef<HTMLButtonElement>(null);
   const scrollArea = useRef<HTMLDivElement>(null);
   const detailHeading = useRef<HTMLHeadingElement>(null);
   const previousCompletion = useRef<string | null | undefined>(undefined);
   const { data: summary, isError: summaryError, fulfilledTimeStamp, refetch: refetchSummary } = useGetImportSummaryQuery(undefined, {
      pollingInterval: pageVisible ? pollInterval : 0, skipPollingIfUnfocused: true,
      refetchOnFocus: true, refetchOnReconnect: true, refetchOnMountOrArgChange: true,
   });
   const { currentData: activity, isFetching, isError, hasNextPage, fetchNextPage, refetch } =
      useGetImportActivityInfiniteQuery(unreadOnly, { skip: !open, refetchOnMountOrArgChange: true });
   const [markRead, { isLoading: markingOne }] = useMarkImportReadMutation();
   const [markAll, { isLoading: markingAll }] = useMarkAllImportsReadMutation();
   const busy = markingOne || markingAll;
   const items = activity?.pages.flatMap(page => page.items) ?? [];
   const selectedId = selected?.id;
   const current = selected ? items.find(item => item.id === selected.id) ?? selected : null;
   const unreadCount = summary?.unreadCount ?? 0;
   const visibleItems = items.filter(item => !unreadOnly || !item.read);
   const localDay = (value: Date) => `${value.getFullYear()}-${value.getMonth()}-${value.getDate()}`;
   const today = new Date();
   const yesterday = new Date(today);
   yesterday.setDate(today.getDate() - 1);
   const dayLabel = (value: string) => {
      const day = new Date(value);
      if (localDay(day) === localDay(today)) return t('today');
      if (localDay(day) === localDay(yesterday)) return t('yesterday');
      return new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(day);
   };
   const groups = Array.from(visibleItems.reduce((days, item) => {
      const key = localDay(new Date(item.receivedAt));
      const group = days.get(key) ?? { label: dayLabel(item.receivedAt), items: [] as ImportAttempt[] };
      group.items.push(item);
      days.set(key, group);
      return days;
   }, new Map<string, { label: string; items: ImportAttempt[] }>()).entries());

   useEffect(() => {
      const visibilityChanged = () => {
         const visible = document.visibilityState !== 'hidden';
         setPageVisible(visible);
         if (visible) refetchSummary();
      };
      document.addEventListener('visibilitychange', visibilityChanged);
      return () => document.removeEventListener('visibilitychange', visibilityChanged);
   }, [refetchSummary]);

   useEffect(() => {
      setPollInterval(summaryError ? 300_000 : summary?.pendingCount ? 30_000 : 60_000);
   }, [summaryError, summary?.pendingCount, fulfilledTimeStamp]);

   useEffect(() => {
      const completed = summary?.latestCompletedAt;
      if (previousCompletion.current !== undefined && completed !== previousCompletion.current) {
         if (open) refetch();
         dispatch(invoicesApi.util.invalidateTags(['Invoice']));
      }
      previousCompletion.current = completed;
   }, [summary?.latestCompletedAt, open, refetch, dispatch]);

   useEffect(() => {
      if (!open) return;
      closeButton.current?.focus();
      const outside = (event: MouseEvent) => {
         if (!container.current?.contains(event.target as Node)) setOpen(false);
      };
      const escape = (event: KeyboardEvent) => {
         if (event.key === 'Escape') { setOpen(false); bell.current?.focus(); }
      };
      document.addEventListener('mousedown', outside);
      document.addEventListener('keydown', escape);
      return () => {
         document.removeEventListener('mousedown', outside);
         document.removeEventListener('keydown', escape);
      };
   }, [open]);

   useEffect(() => {
      if (selectedId != null) detailHeading.current?.focus();
   }, [selectedId]);

   const close = () => { setOpen(false); bell.current?.focus(); };
   const toggle = () => {
      if (!open) { setSelected(null); setActionError(false); }
      setOpen(value => !value);
   };
   const date = (value: string) => new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'medium', timeStyle: 'short',
   }).format(new Date(value));
   const listDate = (value: string) => {
      const time = new Intl.DateTimeFormat(i18n.language, { timeStyle: 'short' }).format(new Date(value));
      return localDay(new Date(value)) === localDay(today) ? time : `${dayLabel(value)}, ${time}`;
   };
   const reason = (code?: string) => t(`issues.${code}`, { defaultValue: t('issues.PROCESSING_FAILED') });
   const summaryText = (item: ImportAttempt) => item.status === 'PARTIALLY_SUCCEEDED'
      ? t('partialSummary', { imported: t('invoicesImported', { count: item.importedCount }), count: item.issues.length })
      : item.status === 'SUCCEEDED' ? t('importedCount', { count: item.importedCount }) : reason(item.issues[0]?.code);

   const acknowledge = async (item?: ImportAttempt) => {
      setActionError(false);
      try {
         if (item) await markRead(item.id).unwrap();
         else await markAll().unwrap();
         // Update read styling after acknowledgement; the invalidated summary supplies the authoritative badge count.
         for (const filter of [false, true]) {
            dispatch(importAttemptsApi.util.updateQueryData('getImportActivity', filter, value => {
               value.pages.forEach(page => page.items.forEach(entry => {
                  if ((!item || entry.id === item.id) && isImportIssue(entry)) entry.read = true;
               }));
            }));
         }
         setSelected(value => value && (!item || item.id === value.id) ? { ...value, read: true } : value);
      } catch { setActionError(true); }
   };

   const openItem = (item: ImportAttempt) => {
      setSelected(item);
      setActionError(false);
      scrollArea.current?.scrollTo?.(0, 0);
      if (isImportIssue(item) && !item.read) void acknowledge(item);
   };

   return <div className="notification-center" ref={container}>
      <button ref={bell} type="button" className="notification-bell" onClick={toggle}
         aria-label={t('bellLabel', { count: unreadCount }) ?? ''} aria-expanded={open} aria-haspopup="dialog">
         <Bell size={23} />
         {unreadCount > 0 && <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>
      {open && <section className="notification-panel" role="dialog" aria-labelledby="notification-title">
         <header className="notification-header">
            <div className="notification-heading">
               {current ? <button className="notification-text-button" onClick={() => setSelected(null)}>
                  <ChevronLeft /> {t('title')}
               </button> : <h2 id="notification-title">{t('title')}</h2>}
               <button ref={closeButton} className="notification-close" onClick={close} aria-label={t('close') ?? ''}><X size={24} /></button>
            </div>
            {!current && <div className="notification-toolbar">
               <div className="notification-tabs" role="group" aria-label={t('filter') ?? ''}>
                  <button aria-pressed={!unreadOnly} className={!unreadOnly ? 'selected' : ''} onClick={() => {
                     setUnreadOnly(false); scrollArea.current?.scrollTo?.(0, 0);
                  }}>{t('allActivity')}</button>
                  <button aria-pressed={unreadOnly} className={unreadOnly ? 'selected' : ''} onClick={() => {
                     setUnreadOnly(true); scrollArea.current?.scrollTo?.(0, 0);
                  }}>{t('unreadIssues')}{unreadCount > 0 && <span>{unreadCount}</span>}</button>
               </div>
               <button className="notification-text-button" disabled={!unreadCount || busy}
                  onClick={() => acknowledge()}>{t('markAllRead')}</button>
            </div>}
         </header>
         {actionError && <div className="notification-error" role="alert">{t('actionError')}{' '}
            <button className="notification-text-button" disabled={busy} onClick={() => acknowledge(current ?? undefined)}>{t('retry')}</button>
         </div>}
         <div className="notification-scroll" ref={scrollArea} aria-busy={isFetching}>
            {current ? <div className="notification-detail">
               <h2 id="notification-title" ref={detailHeading} tabIndex={-1}>{current.filename}</h2>
               <div className={`notification-status status-${current.status}`}>{t(`statuses.${current.status}`)}</div>
               <dl>
                  <dt>{t('received')}</dt><dd>{date(current.receivedAt)}</dd>
                  <dt>{t('source')}</dt><dd>{t(`sources.${current.source}`)}</dd>
                  {current.uploadedBy && <><dt>{t('uploadedBy')}</dt><dd>{current.uploadedBy}</dd></>}
               </dl>
               {current.importedCount > 0 && <p>{t('importedCount', { count: current.importedCount })}</p>}
               {current.issues.length > 0 && <ul className="notification-issues">
                  {current.issues.map((issue, index) => <li key={`${issue.code}-${index}`}>
                     <strong>{reason(issue.code)}</strong>
                     {issue.page != null && <small>{t('page', { page: issue.page })}</small>}
                  </li>)}
               </ul>}
               {current.status === 'FAILED' && <p>{t('noInvoiceCreated')}</p>}
               {current.issues.length > 0 && <div className="notification-guidance">
                  <h3>{t('guidanceTitle')}</h3>
                  <p>{t(`guidance.${current.issues[0].code}`, { defaultValue: t('guidance.PROCESSING_FAILED') })}</p>
               </div>}
            </div> : <>
               {(isError || summaryError) && <div className="notification-error" role="alert">
                  {t('loadError')} <button className="notification-text-button" onClick={() => { refetch(); refetchSummary(); }}>{t('retry')}</button>
               </div>}
               {isFetching && !activity && <p className="notification-empty" role="status">{t('loading')}</p>}
               {!isFetching && !isError && visibleItems.length === 0
                  && <p className="notification-empty">{t(unreadOnly ? 'noUnreadIssues' : 'noActivity')}</p>}
               {groups.map(([day, group]) => <section className="notification-day" key={day} aria-label={group.label}>
                  <h3 className="notification-day-title">{group.label}</h3>
                  <ul className="notification-list">
                     {group.items.map(item => <li key={item.id}
                        className={`${isImportIssue(item) && !item.read ? 'unread' : ''} ${item.status === 'FAILED' ? 'failed' : ''}`}>
                        <button className="notification-row" onClick={() => openItem(item)}>
                           <span className="notification-unread-dot" aria-hidden="true" />
                           <span className={`notification-symbol status-${item.status}`}>
                              {item.status === 'SUCCEEDED' ? <CheckCircle size={16} /> : <ExclamationCircle size={16} />}
                           </span>
                           <span className="notification-row-content">
                              <strong className="notification-filename">{item.filename}</strong>
                              <span className={`notification-status status-${item.status}`}>{t(`statuses.${item.status}`)}</span>
                              <span className="notification-reason" title={summaryText(item)}>{summaryText(item)}</span>
                              <small className="notification-metadata">
                                 {item.source === 'EMAIL' ? <Envelope aria-hidden="true" /> : item.source === 'UPLOAD'
                                    ? <Upload aria-hidden="true" /> : <ArrowRepeat aria-hidden="true" />}
                                 <span>{t(`sources.${item.source}`)}</span><span aria-hidden="true">·</span>
                                 <time dateTime={item.receivedAt}>{listDate(item.receivedAt)}</time>
                              </small>
                           </span>
                           <ChevronRight className="notification-chevron" />
                        </button>
                     </li>)}
                  </ul>
               </section>)}
               {hasNextPage && <button className="notification-load-more" disabled={isFetching}
                  onClick={() => fetchNextPage()}>{t(isFetching ? 'loading' : 'loadOlder')}</button>}
            </>}
         </div>
      </section>}
   </div>;
};

export default NotificationBell;
